import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from "framer-motion";
import {
  Bot,
  ChevronLeft,
  Package,
  Terminal,
  Check,
  Copy,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/ui/page";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Globe as CobeGlobe } from "@/components/ui/cobe-globe";
import { HeroBuildDemo } from "@/components/ui/hero-build-demo";
import { GridPattern } from "@/components/ui/grid-pattern";
import Integrations, { type IntegrationItem } from "@/components/ui/integrations-three";
import { AGENT_SETUP_PROMPT, copyText } from "@/components/ui/agent-setup-prompt";
import { cn } from "@/lib/utils";
import { AppleIcon, WindowsIcon } from "@/components/PlatformIcons";

/* ─────────────────────────────────────────────────────────────
   Home page — warm light surface with orange as the single
   saturated accent. Everything here is scoped to `.lv-home`
   (see index.css) so the rest of the site keeps its own tokens.
   ───────────────────────────────────────────────────────────── */

const TEHRAN: [number, number] = [35.6892, 51.389];

const DC_MARKERS = [
  { id: "tehran", location: TEHRAN, label: "Tehran" },
  { id: "falkenstein", location: [50.4771, 12.3713] as [number, number], label: "Falkenstein" },
  { id: "helsinki", location: [60.1699, 24.9384] as [number, number], label: "Helsinki" },
  { id: "amsterdam", location: [52.3676, 4.9041] as [number, number], label: "Amsterdam" },
  { id: "frankfurt", location: [50.1109, 8.6821] as [number, number], label: "Frankfurt" },
  { id: "paris", location: [48.8566, 2.3522] as [number, number], label: "Paris" },
  { id: "london", location: [51.5074, 359.8722] as [number, number], label: "London" },
  { id: "madrid", location: [40.4168, 356.2962] as [number, number], label: "Madrid" },
  { id: "stockholm", location: [59.3293, 18.0686] as [number, number], label: "Stockholm" },
  { id: "toronto", location: [43.6532, 280.6168] as [number, number], label: "Toronto" },
  { id: "silicon-valley", location: [37.3875, 237.9425] as [number, number], label: "Silicon Valley" },
];

const DC_LOCATIONS = [
  "تهران",
  "فالکنشتاین",
  "هلسینکی",
  "آمستردام",
  "فرانکفورت",
  "پاریس",
  "لندن",
  "مادرید",
  "استکهلم",
  "تورنتو",
  "سیلیکون‌ولی",
];

const DC_ARCS = DC_MARKERS.filter((m) => m.id !== "tehran").map((m) => ({
  id: `ir-${m.id}`,
  from: TEHRAN,
  to: m.location,
}));

const H2 =
  "text-balance text-[1.5rem] font-extrabold leading-[1.5] tracking-[-0.02em] text-ink-950 sm:text-[1.75rem] md:text-[2rem] md:leading-[1.4]";
const LEAD =
  "mt-4 text-pretty text-[15px] leading-[1.95] text-ink-600 sm:text-[16px] sm:leading-[1.9]";

const WAYS = [
  {
    title: "با هوش مصنوعی",
    desc: "گردو را به ChatGPT، Claude یا Cursor وصل کن و با یک پیام سرویس بساز و دیپلوی کن.",
  },
  {
    title: "با خط فرمان",
    desc: "داخل پوشه‌ی پروژه فقط gerdoo deploy بزن؛ ساخت و انتشار خودکار انجام می‌شود.",
  },
  {
    title: "با آپلود پروژه",
    desc: "فایل پروژه را در پنل بکش و رها کن؛ بقیه‌ی کارها با گردوست.",
  },
];

const GUIDE = [
  {
    title: "دیپلوی را به ایجنت می‌سپارم",
    desc: "Gerdoo MCP را متصل کنید تا ایجنت سرویس بسازد، دیپلوی کند و لاگ‌ها را بررسی کند.",
    href: "/agents",
  },
  {
    title: "با Git یا CLI دیپلوی می‌کنم",
    desc: "مخزن، Docker Image یا پوشه پروژه را متصل و نسخه‌های جدید را منتشر کنید.",
    href: "/products/paas",
  },
  {
    title: "ایجنت‌های هوش مصنوعی",
    desc: "عامل‌های هوشمند را با اتصال به ابزارها و مدل‌های زبانی راه‌اندازی کنید.",
    href: "/agents",
  },
  {
    title: "سرویس آماده نیاز دارم",
    desc: "دیتابیس و نرم‌افزارهای موردنیاز پروژه را بدون نصب دستی راه‌اندازی کنید.",
    href: "/marketplace",
  },
];

/** Section wrapper for the home page — no tone switching, one warm surface. */
function Band({
  children,
  className = "",
  id,
  wide = false,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  /** Roomier column — used where cards carry full mock visuals. */
  wide?: boolean;
}) {
  return (
    <section id={id} className={`py-24 sm:py-32 ${className}`}>
      <div className={`mx-auto px-6 sm:px-10 ${wide ? "max-w-[1340px]" : "max-w-[1160px]"}`}>
        {children}
      </div>
    </section>
  );
}

function SectionIcon({ icon: Icon }: { icon: React.ComponentType<{ className?: string }> }) {
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-gerdoo-50 text-gerdoo-600 ring-1 ring-inset ring-gerdoo-500/15">
      <Icon className="h-5 w-5" aria-hidden />
    </span>
  );
}

function Hero() {
  // Fills the viewport; the top padding clears the fixed navbar (h-14 /
  // sm:3.25rem) so the content is optically centered under it.
  return (
    <section className="relative flex min-h-svh items-center overflow-hidden pt-24 pb-10 sm:pt-28 sm:pb-12">
      {/* Background: a skewed grid with a few filled cells, fading out from the centre. */}
      <GridPattern
        squares={[
          [4, 4], [5, 1], [8, 2], [5, 3], [5, 5], [10, 10], [12, 15],
          [15, 10], [10, 15], [18, 6], [21, 3], [24, 9], [27, 5], [2, 9],
        ]}
        className={cn(
          "[mask-image:radial-gradient(900px_circle_at_center,white,transparent)]",
          "inset-x-0 inset-y-[-30%] h-[200%] skew-y-12",
        )}
      />
      <div className="relative mx-auto w-full max-w-[1040px] px-6 text-center sm:px-10">
        <h1 className="animate-rise-in text-balance text-[2rem] font-extrabold leading-[1.35] tracking-[-0.025em] text-ink-950 sm:text-[2.6rem] sm:leading-[1.3] md:text-[3.25rem] md:leading-[1.25]">
          با هوش مصنوعی ساختی؟
          <br className="hidden sm:block" />{" "}
          با گردو <span className="text-[#f6821f]">آنلاین‌ش</span> کن.
        </h1>

        <p className="animate-rise-in animate-rise-in-delay-1 mx-auto mt-5 max-w-[560px] text-balance text-[14px] leading-[1.95] text-ink-600 sm:text-[16px] sm:leading-[1.9]">
          فقط بگو چی می‌خوای. گردو سرویس‌ها، پایگاه داده و دامنه رو می‌سازه، به هم وصل
          می‌کنه و همه رو روی یک نقشه جلوی چشمت آنلاین می‌کنه.
        </p>

        <div className="animate-rise-in animate-rise-in-delay-2 mt-10">
          {/* Trying the compact input pinned to the map's corner ("card" = the large composer, "none" = map only). */}
          <HeroBuildDemo composer="corner" />
        </div>

        <div className="animate-rise-in animate-rise-in-delay-2 mt-7">
          {/* The two ways to work with an AI — the desktop app, or your own
              agent — in the header buttons' shape, a size up. */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/download"
              className="inline-flex h-11 items-center justify-center gap-2.5 rounded-md bg-ink-950 px-5 text-[14px] font-semibold leading-none text-white no-underline transition-colors hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gerdoo-400 focus-visible:ring-offset-2"
            >
              <span className="flex items-center gap-1.5" aria-hidden>
                <AppleIcon className="h-5 w-5" />
                <WindowsIcon className="h-[18px] w-[18px]" />
              </span>
              دانلود اپلیکیشن گردو
            </Link>
            <AgentSetupButton />
          </div>
          <div className="mt-3">
            <Link
              to="/agents"
              className="inline-flex items-center gap-1 text-[13px] font-bold text-gerdoo-700 transition-opacity hover:opacity-70"
            >
              جزئیات بیشتر درباره‌ی کار با ایجنت‌ها
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Spark directions for the copy burst: evenly spread, slightly jittered. */
const SPARKS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i / 12) * Math.PI * 2 + (i % 2 ? 0.18 : -0.12);
  const dist = 34 + (i % 3) * 12;
  return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, size: i % 3 === 0 ? 7 : 5, delay: (i % 4) * 0.015 };
});

/**
 * The hero's agent call-to-action: one click copies the setup prompt, which the
 * visitor pastes into whichever coding agent they use. Copying is celebrated:
 * the button pops, a ring and a burst of sparks leave the icon, a shine sweeps
 * across, the robot spins into a check and the label rolls over to "copied".
 */
function AgentSetupButton() {
  const [copied, setCopied] = useState(false);
  // Each click restarts the one-shot effects by remounting them under a new key.
  const [burst, setBurst] = useState(0);
  const reduced = useReducedMotion();
  const pop = useAnimationControls();
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    if (!(await copyText(AGENT_SETUP_PROMPT))) return;
    setCopied(true);
    setBurst((b) => b + 1);
    if (!reduced) void pop.start({ scale: [1, 1.07, 0.98, 1], transition: { duration: 0.45, ease: "easeOut" } });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2400);
  };

  return (
    <motion.button
      type="button"
      layout
      onClick={() => void copy()}
      title={AGENT_SETUP_PROMPT}
      aria-label={`کپی متن راه‌اندازی برای ایجنت: ${AGENT_SETUP_PROMPT}`}
      whileHover={reduced ? undefined : { y: -1 }}
      whileTap={reduced ? undefined : { scale: 0.95 }}
      animate={pop}
      transition={{ layout: { type: "spring", stiffness: 420, damping: 32 } }}
      className={cn(
        // The header buttons' shape, a size up, in the brand orange.
        "group relative inline-flex h-11 items-center gap-2.5 overflow-visible rounded-md px-5 text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gerdoo-400 focus-visible:ring-offset-2",
        copied ? "bg-emerald-500" : "bg-[#f6821f] hover:bg-gerdoo-600",
      )}
    >
      {/* Shine sweeping across the pill, clipped to its shape */}
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-md" aria-hidden>
        {burst > 0 && !reduced && (
          <motion.span
            key={`shine-${burst}`}
            className="absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/80 to-transparent"
            initial={{ left: "-40%" }}
            animate={{ left: "120%" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
        )}
      </span>

      <motion.span
        layout="position"
        className={cn(
          "relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-white",
        )}
      >
        {/* Ring and sparks leaving the icon */}
        {burst > 0 && !reduced && (
          <span key={`fx-${burst}`} className="pointer-events-none absolute inset-0" aria-hidden>
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-[#f6821f]"
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 2.6, opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
            {SPARKS.map((sp, i) => (
              <motion.span
                key={i}
                className={cn("absolute left-1/2 top-1/2 rounded-full", i % 3 === 1 ? "bg-amber-400" : i % 3 === 2 ? "bg-emerald-400" : "bg-[#f6821f]")}
                style={{ width: sp.size, height: sp.size, marginLeft: -sp.size / 2, marginTop: -sp.size / 2 }}
                initial={{ x: 0, y: 0, scale: 0.4, opacity: 1 }}
                animate={{ x: sp.x, y: sp.y, scale: [0.4, 1.15, 0], opacity: [1, 1, 0] }}
                transition={{ duration: 0.65, delay: sp.delay, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}
          </span>
        )}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={copied ? "check" : "bot"}
            initial={reduced ? { opacity: 0 } : { rotate: -90, scale: 0.3, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={reduced ? { opacity: 0 } : { rotate: 90, scale: 0.3, opacity: 0 }}
            transition={{ type: "spring", stiffness: 520, damping: 22 }}
            className="relative flex"
          >
            {copied ? <Check className="h-[18px] w-[18px]" aria-hidden /> : <Bot className="h-[18px] w-[18px]" aria-hidden />}
          </motion.span>
        </AnimatePresence>
      </motion.span>

      {/* Label rolls over */}
      <span className="relative overflow-hidden py-0.5">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={copied ? "copied" : "idle"}
            className="block whitespace-nowrap text-[14px] font-semibold leading-none text-white"
            initial={reduced ? { opacity: 0 } : { y: "110%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduced ? { opacity: 0 } : { y: "-110%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
          >
            {copied ? "کپی شد؛ به ایجنت خود بدهید" : "استقرار با ایجنت‌های هوش مصنوعی"}
          </motion.span>
        </AnimatePresence>
      </span>

      <motion.span layout="position" className="relative flex" aria-hidden>
        {copied ? (
          <Check className="h-4 w-4 text-white" />
        ) : (
          <Copy className="h-4 w-4 text-white/80 transition-transform group-hover:scale-110" />
        )}
      </motion.span>
      <span className="sr-only" aria-live="polite">{copied ? "متن راه‌اندازی کپی شد" : ""}</span>
    </motion.button>
  );
}

/** True while the returned ref's element is on screen. Never true for
 *  reduced-motion users, so every mock stays in its finished state. */
function useOnScreen<T extends HTMLElement>(threshold = 0.35) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, visible };
}

/** Tinted stage every card's mock sits on. */
function WayStage({
  children,
  stageRef,
}: {
  children: React.ReactNode;
  stageRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={stageRef}
      className="relative h-[264px] overflow-hidden border-b border-black/[0.06] bg-gerdoo-50/60"
    >
      {children}
    </div>
  );
}

const AGENT_MSG = "سایت شرکت رو دیپلوی کن";

/** With an agent — the prompt types itself, then the reply lands. */
function WayAgentMock() {
  const { ref, visible } = useOnScreen<HTMLDivElement>();
  // Server render and reduced motion show the finished exchange.
  const [chars, setChars] = useState(AGENT_MSG.length);
  const [reply, setReply] = useState(true);

  useEffect(() => {
    if (!visible) return;
    let timers: number[] = [];

    const run = () => {
      timers = [];
      setChars(0);
      setReply(false);
      for (let i = 1; i <= AGENT_MSG.length; i++) {
        timers.push(window.setTimeout(() => setChars(i), 500 + i * 60));
      }
      const typed = 500 + AGENT_MSG.length * 60;
      timers.push(window.setTimeout(() => setReply(true), typed + 850));
      timers.push(window.setTimeout(run, typed + 850 + 4200));
    };

    run();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [visible]);

  return (
    <WayStage stageRef={ref}>
      <div className="absolute inset-x-6 inset-y-0 flex items-center">
        <div className="w-full space-y-2.5 rounded-lg border border-black/[0.06] bg-white p-4 shadow-[0_18px_40px_-18px_rgb(66_40_14_/_0.35)]">
          <div className="flex items-center justify-center gap-2 pb-1 pt-0.5">
            {AGENTS.map((a) => (
              <span
                key={a.name}
                title={a.name}
                className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md bg-white shadow-[0_4px_10px_-4px_rgb(66_40_14_/_0.25)] ring-1 ring-inset ring-black/[0.08]"
              >
                <img
                  src={a.src}
                  alt={a.name}
                  loading="lazy"
                  className="h-7 w-7 rounded-[6px] object-contain"
                />
              </span>
            ))}
          </div>

          <p className="min-h-[2.1rem] w-fit max-w-[85%] rounded-lg rounded-ss-sm bg-gerdoo-50 px-3 py-2 text-[12.5px] leading-[1.8] text-gerdoo-800">
            {AGENT_MSG.slice(0, chars)}
            {chars < AGENT_MSG.length && (
              <span className="lv-caret ms-0.5 inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-gerdoo-500" />
            )}
          </p>

          <div className="min-h-[3.4rem]">
            {reply && (
              <div className="lv-in ms-auto w-fit max-w-[92%] rounded-lg rounded-se-sm bg-ink-100 px-3 py-2">
                <p className="text-[10.5px] font-bold text-ink-500" dir="ltr">
                  Gerdoo
                </p>
                <p className="mt-1 text-[12.5px] leading-[1.8] text-ink-700">
                  سرویس ساخته شد و روی دامنه بالا آمد.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </WayStage>
  );
}

const CLI_CMD = "gerdoo deploy";
const CLI_LOG = [
  { text: "→ Detected Next.js 15", ok: false },
  { text: "→ Building image · 24s", ok: false },
  { text: "→ Pushing release", ok: false },
  { text: "✓ Live at company.gerdoo.app", ok: true },
];

/** With the CLI — the command types itself, then the log streams in. */
function WayCliMock() {
  const { ref, visible } = useOnScreen<HTMLDivElement>();
  const [chars, setChars] = useState(CLI_CMD.length);
  const [lines, setLines] = useState(CLI_LOG.length);

  useEffect(() => {
    if (!visible) return;
    let timers: number[] = [];

    const run = () => {
      timers = [];
      setChars(0);
      setLines(0);
      for (let i = 1; i <= CLI_CMD.length; i++) {
        timers.push(window.setTimeout(() => setChars(i), 500 + i * 70));
      }
      const typed = 500 + CLI_CMD.length * 70;
      CLI_LOG.forEach((_, i) =>
        timers.push(window.setTimeout(() => setLines(i + 1), typed + 500 + i * 620)),
      );
      const done = typed + 500 + CLI_LOG.length * 620;
      timers.push(window.setTimeout(run, done + 3200));
    };

    run();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [visible]);

  return (
    <WayStage stageRef={ref}>
      <div className="absolute inset-x-7 bottom-0 top-8 overflow-hidden rounded-t-2xl border border-b-0 border-black/[0.06] bg-white shadow-[0_18px_40px_-20px_rgb(66_40_14_/_0.3)]">
        <div className="flex items-center gap-1.5 border-b border-black/[0.06] px-4 py-2.5" dir="ltr">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]" />
        </div>
        <div className="px-4 py-4 font-mono text-[11px] leading-[2.1]" dir="ltr">
          <p className="font-bold text-ink-900">
            <span className="text-gerdoo-600">$</span> {CLI_CMD.slice(0, chars)}
            {chars < CLI_CMD.length && (
              <span className="lv-caret ms-px inline-block h-[1em] w-[6px] translate-y-[0.15em] bg-ink-800" />
            )}
          </p>
          {CLI_LOG.slice(0, lines).map((l) => (
            <p
              key={l.text}
              className={l.ok ? "lv-in font-bold text-gerdoo-700" : "lv-in text-ink-400"}
            >
              {l.text}
            </p>
          ))}
        </div>
      </div>
    </WayStage>
  );
}

/** By uploading — the project file drops into the zone, then lands. */
function WayUploadMock() {
  const { ref, visible } = useOnScreen<HTMLDivElement>();
  // 0 idle · 1 file falling in · 2 uploaded
  const [phase, setPhase] = useState(2);

  useEffect(() => {
    if (!visible) return;
    let timers: number[] = [];

    const run = () => {
      timers = [];
      setPhase(0);
      timers.push(window.setTimeout(() => setPhase(1), 900));
      timers.push(window.setTimeout(() => setPhase(2), 2100));
      timers.push(window.setTimeout(run, 5200));
    };

    run();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [visible]);

  return (
    <WayStage stageRef={ref}>
      <div className="absolute inset-x-6 inset-y-8 overflow-hidden rounded-lg border border-black/[0.06] bg-white p-4 shadow-[0_18px_40px_-20px_rgb(66_40_14_/_0.3)]">
        <div
          className={
            phase === 1
              ? "flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gerdoo-500 bg-gerdoo-100/70 transition-colors duration-300"
              : "flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gerdoo-400 bg-gerdoo-50/50 transition-colors duration-300"
          }
        >
          {phase === 2 ? (
            <span className="lv-in flex h-10 w-10 items-center justify-center rounded-full bg-gerdoo-500 text-white">
              <Check className="h-5 w-5" aria-hidden />
            </span>
          ) : (
            <span className={phase === 1 ? "lv-drop block" : "block"}>
              <Package className="h-6 w-6 text-gerdoo-600" aria-hidden />
            </span>
          )}
          <p className="mt-2.5 text-[12.5px] font-semibold text-ink-700">
            {phase === 2 ? "پروژه آپلود شد" : "فایل پروژه را اینجا رها کنید"}
          </p>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent"
        aria-hidden
      />
    </WayStage>
  );
}

const AGENTS = [
  { name: "Claude", src: "/agents/claude.png" },
  { name: "ChatGPT", src: "/agents/chatgpt.webp" },
  { name: "Cursor", src: "/agents/cursor.png" },
  { name: "Antigravity", src: "/agents/antigravity.png" },
];

const WAY_MOCKS = [WayAgentMock, WayCliMock, WayUploadMock];

function PublishWays() {
  return (
    <Band wide>
      <Reveal className="max-w-[640px] text-start">
        <h2 className={H2}>سه راه برای آنلاین کردن پروژه</h2>
        <p className={LEAD}>
          با ایجنت هوش مصنوعی، با خط فرمان، یا با آپلود مستقیم پروژه، هر کدام که برایت
          راحت‌تر است.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {WAYS.map((w, i) => {
          const Mock = WAY_MOCKS[i];
          return (
            <Reveal key={w.title} i={i}>
              <div className="lv-card lv-card-hover h-full overflow-hidden p-0 text-start">
                <Mock />
                <div className="p-6 sm:p-7">
                  <h3 className="text-[17px] font-bold leading-[1.7] tracking-tight text-ink-950">
                    {w.title}
                  </h3>
                  <p className="mt-2 text-[14.5px] leading-[1.95] text-ink-600">{w.desc}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </Band>
  );
}

/** Light "deploy" panel — window chrome over a readable build log. */
function DeployPanel() {
  const log = [
    { text: "$ gerdoo deploy", tone: "cmd" },
    { text: "→ Detected Next.js 15", tone: "dim" },
    { text: "→ Installing dependencies · mirror: ir", tone: "dim" },
    { text: "→ Building image · 24s", tone: "dim" },
    { text: "✓ Live at https://my-shop.gerdoo.app", tone: "ok" },
  ];
  return (
    <div className="lv-card mx-auto max-w-[720px] overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-black/[0.06] bg-gerdoo-50/50 px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        <span className="ms-3 text-[12px] font-semibold text-ink-500" dir="ltr">
          my-shop — deploy
        </span>
      </div>
      <div className="px-6 py-6 text-start font-mono text-[13px] leading-[2]" dir="ltr">
        {log.map((l) => (
          <p
            key={l.text}
            className={
              l.tone === "cmd"
                ? "font-bold text-ink-900"
                : l.tone === "ok"
                  ? "font-bold text-gerdoo-700"
                  : "text-ink-500"
            }
          >
            {l.text}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * Commented out of the page for now — kept here (and exported so the unused
 * check stays quiet) so it can be dropped back into <Landing> when wanted.
 */
export function PaaSChapter() {
  return (
    <Band id="paas" className="scroll-mt-16">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal className="text-start">
          <SectionIcon icon={Terminal} />
          <h2 className={`${H2} mt-6`}>از کد تا اجرا، بدون پیچیدگی زیرساخت</h2>
          <p className={LEAD}>
            پروژه را از Git، Docker یا CLI به گردو بسپارید. گردو کد را می‌سازد، سرویس را
            اجرا می‌کند و دامنه، SSL و نسخه‌های بعدی را در یک مسیر یکپارچه مدیریت می‌کند.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              "تشخیص خودکار استک یا ساخت از Dockerfile خودتان",
              "نسخه‌ها، بازگشت به نسخه قبل و محیط‌های جدا",
              "متغیرها و رازها با اولویت پروژه، محیط و سرویس",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] leading-[1.9] text-ink-700">
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gerdoo-100 text-gerdoo-700">
                  <Check className="h-3 w-3" aria-hidden />
                </span>
                {t}
              </li>
            ))}
          </ul>
          <Link
            to="/products/paas"
            className="mt-8 inline-flex items-center gap-1 text-[15px] font-bold text-gerdoo-700 transition-opacity hover:opacity-70"
          >
            درباره سکوی ابری
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Link>
        </Reveal>

        <Reveal i={1}>
          <DeployPanel />
        </Reveal>
      </div>
    </Band>
  );
}

/* ── Platform section ───────────────────────────────────────────
   Everything an app needs, one card per product surface.
   ─────────────────────────────────────────────────────────────── */

const PLATFORM: IntegrationItem[] = [
  {
    title: "اجرا و میزبانی، انجام‌شده",
    description: "بیلد، اجرا، دامنه و SSL با گردوست؛ کد و داده‌ی شما همیشه مال خودتان می‌ماند.",
    href: "/products/paas",
    image: "/platform/hosting.webp",
  },
  {
    title: "دامنه و شبکه، بدون تنظیم دستی",
    description: "دامنه و DNS را وصل کنید؛ پاسخ از نزدیک‌ترین نقطه به کاربر داده می‌شود.",
    href: "/products/dns",
    image: "/platform/network.webp",
  },
  {
    title: "دیتابیس، ساخته و وصل‌شده",
    description: "PostgreSQL، MySQL، MongoDB و Redis؛ مصرف و ترافیک را همان‌جا ببینید.",
    href: "/products/databases",
    image: "/platform/databases.webp",
  },
  {
    title: "سرویس‌های جانبی، آماده‌ی نصب",
    description: "کش، صف و نرم‌افزارهای آماده را از مارکت‌پلیس کنار اپلیکیشن بیاورید.",
    href: "/marketplace",
    image: "/platform/addons.webp",
  },
  {
    title: "ایمیل، روی دامنه‌ی خودتان",
    description: "ایمیل‌های اپلیکیشن و تیم را از همان دامنه بفرستید و وضعیتشان را دنبال کنید.",
    href: "/products/email",
    image: "/platform/email.webp",
  },
  {
    title: "کانتینر، با دیسک ماندگار",
    description: "هر ایمیج داکری را با دیسک ماندگار، بکاپ خودکار و مقیاس‌پذیری اجرا کنید.",
    href: "/products/containers",
    image: "/platform/compute.webp",
  },
];

function PlatformRail() {
  return (
    <Integrations
      title="هر آنچه اپلیکیشن شما نیاز دارد، در یک پلتفرم"
      description="گردو روی زیرساخت آماده‌ی اجرا می‌چرخد تا شما فقط نرم‌افزار را بسازید."
      items={PLATFORM}
    />
  );
}


function Infrastructure() {
  return (
    <Band>
      <div className="relative overflow-hidden rounded-lg bg-ink-950">
        <span
          className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(60%_100%_at_50%_0%,rgb(246_130_31_/_0.28)_0%,transparent_70%)]"
          aria-hidden
        />
        <div className="relative px-8 pt-14 sm:px-14">
          <Reveal className="mx-auto max-w-[680px] text-center">
            <h2 className={H2.replace("text-ink-950", "text-white")}>
              اجرا در ایران یا دیتاسنترهای بین‌المللی
            </h2>
            <p className={LEAD.replace("text-ink-600", "text-ink-300")}>
              سرویس خود را نزدیک کاربران اجرا کنید و میان زیرساخت داخل ایران و موقعیت‌های
              بین‌المللی جابه‌جا شوید، بدون تغییر در فرایند دیپلوی.
            </p>
            <Link
              to="/datacenters"
              className="mt-6 inline-flex items-center gap-1 text-[15px] font-bold text-gerdoo-400 transition-opacity hover:opacity-70"
            >
              مراکز داده
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Link>
          </Reveal>

          <Reveal i={1} className="mt-10">
            <div className="mx-auto w-full max-w-md sm:max-w-lg">
              <CobeGlobe
                markers={DC_MARKERS}
                arcs={DC_ARCS}
                markerColor={[1, 1, 1]}
                arcColor={[0.96, 0.51, 0.12]}
                baseColor={[0.42, 0.4, 0.38]}
                glowColor={[0.16, 0.15, 0.14]}
                dark={1}
                mapBrightness={13}
                diffuse={1.2}
                mapSamples={24000}
                markerSize={0.035}
                markerElevation={0.025}
                arcWidth={0.6}
              />
            </div>
            <p className="pb-12 text-center text-[13px] leading-[2] text-ink-400">
              {DC_LOCATIONS.join(" · ")}
            </p>
          </Reveal>
        </div>
      </div>
    </Band>
  );
}

/**
 * The "which way in?" chapter. Four routes, as flat rows divided by hairlines
 * rather than cards — the page already spends its card weight on the composer
 * and the way-mocks above.
 */
function Guide() {
  return (
    <Band>
      <Reveal className="max-w-[640px] text-start">
        <h2 className={H2}>برای توسعه‌دهندگان ساخته شده، برای ایجنت‌ها آماده است</h2>
        <p className={LEAD}>
          از هر مسیری که شروع کنید، همان پنل و همان صورتحساب را دارید.
        </p>
      </Reveal>

      <div className="mt-12 border-t border-black/[0.08]">
        {GUIDE.map((g, i) => (
          <Reveal key={g.title} i={i}>
            <Link
              to={g.href}
              className="group flex items-start gap-5 border-b border-black/[0.08] px-1 py-7 text-start transition-colors hover:bg-gerdoo-50/40 sm:gap-8 sm:py-8"
            >
              <span className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-10">
                <span className="block text-[16px] font-bold leading-[1.7] tracking-tight text-ink-950 sm:w-[17rem] sm:shrink-0 sm:text-[17px]">
                  {g.title}
                </span>
                <span className="mt-2 block text-[14.5px] leading-[1.95] text-ink-600 sm:mt-0">
                  {g.desc}
                </span>
              </span>

              <ChevronLeft
                className="mt-1 h-4 w-4 shrink-0 text-ink-300 transition-all group-hover:-translate-x-1 group-hover:text-gerdoo-600"
                aria-hidden
              />
            </Link>
          </Reveal>
        ))}
      </div>
    </Band>
  );
}

export default function Landing() {
  // Smooth scrolling is scoped to this page (and skipped for reduced motion)
  // so anchor jumps and in-page links glide instead of snapping.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = document.documentElement;
    root.classList.add("scroll-smooth");
    return () => root.classList.remove("scroll-smooth");
  }, []);

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://gerdoo.cloud/#organization",
        name: "گردو کلاد",
        url: "https://gerdoo.cloud",
        logo: "https://gerdoo.cloud/favicon.svg",
        description:
          "گردو کلاد، زیرساخت ابری هوشمند برای ساخت و اجرای نرم‌افزار؛ شامل اپلیکیشن، سرور، دیتابیس، DNS و دیپلوی از Git، CLI یا ایجنت.",
      },
      {
        "@type": "WebSite",
        "@id": "https://gerdoo.cloud/#website",
        url: "https://gerdoo.cloud",
        name: "گردو کلاد",
        alternateName: ["gerdoo.cloud", "Gerdoo Cloud", "گردو"],
        publisher: { "@id": "https://gerdoo.cloud/#organization" },
        description:
          "وب‌سایت رسمی گردو کلاد؛ زیرساخت ابری هوشمند برای اپلیکیشن، سرور و دیتابیس در ایران و جهان، با دیپلوی از Git، CLI و ایجنت.",
      },
    ],
  };

  return (
    <div className="lv-home min-h-dvh selection:bg-gerdoo-500/20">
      <SEO
        title="گردو کلاد | زیرساخت ابری برای ساخت و اجرای نرم‌افزار"
        description="گردو کلاد ارائه‌دهنده‌ی خدمات ابری: اپلیکیشن، دیتابیس و سرور خود را در ایران یا جهان اجرا کنید و از Git، CLI یا ایجنت هوش مصنوعی دیپلوی کنید."
        keywords="گردو کلاد, gerdoo, سایت گردو, گردو کلود, خدمات ابری, زیرساخت ابری, کلود ایرانی, ابر ایرانی"
        jsonLd={homeSchema}
      />
      <Navbar />
      <main>
        <Hero />
        <PublishWays />
        <PlatformRail />
        <PaaSChapter />
        <Infrastructure />
        <Guide />
      </main>
      <Footer />
    </div>
  );
}
